import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Checkbox,
  CloseButton,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  RangeSlider,
  RangeSliderFilledTrack,
  RangeSliderThumb,
  RangeSliderTrack,
  Stack,
  Switch,
  Text,
  VStack
} from '@chakra-ui/react'
import type { InsuranceParamsDto, Offer, OfferAddon, OfferLeasing, OfferVehicle, Variant } from 'BrokerTsLib/src/dtos'
import type { Insurer, RiskVariant } from 'BrokerTsLib/src/enums'
import { Product, Risk } from 'BrokerTsLib/src/enums'
import { useMemo } from 'react'

import { useOffersAddonContext } from '@/context/OffersAddonProvider'
import { usePriceFormat } from '@/hooks/usePriceFormat'
import { usePrintContext } from '@/context/PrintContext'
import { useTranslationsContext } from 'BrokerTsLib/src/context/TranslationsContext'
import { useInsuranceParams } from '../context/InsuranceParamsProvider'
import { useOffersVehicleContext } from '../context/OffersVehicleProvider'
import { useGetRisks, useGetRiskVariants } from '../hooks'
import { useApp } from '../hooks/useApp'

interface FilterSectionGroupProps {
  name: string
  children: React.ReactNode
}

export interface FilterOptions {
  insurers?: { insurerId: Insurer, insurerName: string }[]
  riskVariants?: Partial<Record<Risk, RiskVariant[]>>

  fixed?: boolean
  float?: boolean

  fixedComplicity?: boolean
  percentageComplicity?: boolean

  addons?: Risk[]
}

const FilterSectionGroup = ({ name, children }: FilterSectionGroupProps) => {
  return (
    <AccordionItem borderColor={'gray.100'} _first={{ borderTopColor: 'transparent' }}
      sx={{
        gap: { base: 2, md: 4 },
      }}
    >
      <AccordionButton justifyContent={'space-between'} py={2.5} px={2.5}>
        <Text fontSize={'sm'} fontWeight={'semibold'}>{name}</Text>
        <AccordionIcon color={'gray.400'} />
      </AccordionButton>

      <AccordionPanel sx={{ display: 'flex', flexDir: 'column', gap: 2.5, p: 2.5 }} >
        {children}
      </AccordionPanel>
    </AccordionItem >
  )
}

interface CustomVStackProps {
  children: React.ReactNode
}

const CustomVStack = ({ children, ...rest }: CustomVStackProps) => {
  return (
    <VStack align="start" sx={{ gap: { base: 2, md: 3 } }} {...rest}>
      {children}
    </VStack>
  )
}

interface PrintModeButtonsProps {
  offers: Offer[]
  filtered: Offer[]
  addonOffers: Offer[]
  activeAddonOffers: Offer[]
  updateOffer: (offer: Offer) => void
  updateAddonOffer: (offer: Offer) => void
  toPrintCount: number
  translate: (key: string, fallback: string) => string
}

const PrintModeButtons = ({
  offers, filtered, addonOffers, activeAddonOffers,
  updateOffer, updateAddonOffer, toPrintCount, translate
}: PrintModeButtonsProps) => {
  const shownOffers = [...filtered, ...activeAddonOffers]

  return (
    <Stack id='printFilterButtons'>
      <Button px={0} colorScheme="primary"
        onClick={() => {
          setPrintState(filtered, updateOffer, true)
          setPrintState(activeAddonOffers, updateAddonOffer, true)
        }}
        isDisabled={shownOffers.filter(x => !x.print).length == 0}>
        {translate('step2.print.selectShownForPrint', 'Vybrat zobrazené k tisku')} ({shownOffers.filter(x => !x.print).length})
      </Button>

      <Button
        colorScheme="primary"
        onClick={() => {
          setPrintState(offers, updateOffer, false)
          setPrintState(addonOffers, updateAddonOffer, false)
          setPrintState(filtered, updateOffer, true)
          setPrintState(activeAddonOffers, updateAddonOffer, true)
        }}
        isDisabled={shownOffers.filter(x => x.print).length === toPrintCount && toPrintCount > 0}
      >
        {translate('step2.print.printOnlyShown', 'Tisk pouze zobrazených')} ({shownOffers.length})
      </Button>

      {toPrintCount > 0 &&
        <Button colorScheme="gray" onClick={() => {
          setPrintState(filtered, updateOffer, false)
          setPrintState(activeAddonOffers, updateAddonOffer, false)
        }}>
          {translate('step2.print.removeShown', 'Odebrat zobrazené')}
        </Button>
      }

      {[...offers, ...activeAddonOffers].filter(x => x.print).length > 0 &&
        <Button
          colorScheme="gray"
          onClick={() => {
            setPrintState(offers, updateOffer, false)
            setPrintState(activeAddonOffers, updateAddonOffer, false)
          }}
        >
          {translate('removeAll', 'Odebrat vše')}
        </Button>
      }
    </Stack>
  )
}

interface QuotesFilterProps {
  showFilter: boolean
  onCloseClick?: () => void
}

const setPrintState = <T extends { print?: boolean }>(
  items: T[], updateFn: (item: T) => void, value: boolean
) => items.forEach(x => { x.print = value; updateFn(x) })

const setLeasingFilter = (ds: FilterOptions, o: Offer) => {
  const leasing = o as OfferLeasing
  if (leasing.fixedPrice)
    ds.fixed = true
  else
    ds.float = true
}

export const QuotesFilter = ({ showFilter = false, onCloseClick }: QuotesFilterProps) => {
  const { product } = useApp()
  const { printMode } = usePrintContext()
  const { translate } = useTranslationsContext()
  const { priceFormat } = usePriceFormat()

  const { insuranceParams: ip } = useInsuranceParams()
  const insuranceParams = ip as InsuranceParamsDto
  const {
    offers,
    updateOffer,
    showAll,
    showWithErrors,
    filtered,
    priceRange,
    priceRangeFilter,
    handleChangeFilterFixed,
    handleChangeFilterFixedComplicity,
    handleChangeFilterInsurer,
    handleChangeFilterRiskVariant,
    handleChangeFilterRisk,
    handleChangeFilterPriceRange,
    setShowWithErrors,
    setShowAll,
    filter,
    resetFilter,
    filterAplied
  } = useOffersVehicleContext()

  const { offers: addonOffers, updateOffer: updateAddonOffer } = useOffersAddonContext()
  const activeAddonOffers = addonOffers.filter(x => x.isActive && x.errors.filter(y => !y.softError).length == 0 && x.price > 0) ?? []

  const dataSource = useMemo(() => {
    const filteredOffers = !showAll ? offers.filter(x => x.included) : offers
    return getQuotesFilterDS(filteredOffers, insuranceParams?.baseParams.variants, product == Product.VehicleLeasingInsurance ? setLeasingFilter : undefined)
  }, [offers, showAll, insuranceParams?.baseParams.variants, product])

  const { data: dataRisks } = useGetRisks()
  const { data: dataRisksVariants } = useGetRiskVariants(undefined)

  const handleClose = () => {
    if (onCloseClick) {
      onCloseClick()
    }
  }

  const anyErrorQuote = offers.filter(x => x.errors.filter(y => !y.softError).length > 0 || x.price == 0).length > 0
  const anyHidden = offers.filter(x => !x.included).length > 0

  const toPrintCount = [...offers, ...activeAddonOffers].filter(x => x.print && x.errors.filter(y => !y.softError).length == 0 && x.price > 0).map(x => x.offerId).length

  return (
    <Grid
      sx={{
        display: {
          base: showFilter ? 'grid' : 'none',
          md: 'grid',
        },
        alignContent: 'start',
        position: { base: 'fixed', md: 'static' },
        bottom: { base: 4, md: 'auto' },
        left: { base: 4, md: 'auto' },
        width: { base: 'calc(100% - 32px)', md: 'auto' },
        zIndex: 10
      }}
    >
      <CloseButton
        sx={{
          display: {
            base: 'block',
            md: 'none',
          },
          position: {
            base: 'absolute',
            md: 'static',
          },
          top: 4,
          right: 4,
          zIndex: 11,
        }}
        onClick={handleClose}
      />

      <Box
        sx={{
          display: {
            base: 'block',
            md: 'none',
          },
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          bg: 'blackAlpha.500',
          zIndex: -1,
        }}
        onClick={handleClose}
      />

      <Accordion allowMultiple defaultIndex={[0]}
        sx={{
          gap: 6,
          position: 'relative',
          maxHeight: { base: 'calc(100vh - 72px)', md: 'auto' },
          overflowY: { base: 'auto', md: 'hidden' },
          p: { base: 4, md: 0 },
          bg: {
            base: 'white',
            md: 'transparent',
          },
          borderRadius: {
            base: 'md',
            md: 'none',
          },
          boxShadow: {
            base: 'xl',
            md: 'none',
          },
          zIndex: 10,
          pr: { base: 4, md: '3px' },
        }}
      >

        {dataSource.insurers &&
          <FilterSectionGroup name={translate('insurers', 'Pojišťovny')}>
            <CustomVStack>
              {dataSource.insurers.map(insurer => {
                return (
                  <Checkbox
                    key={insurer.insurerId}
                    size='sm'
                    value={insurer.insurerId.toString()}
                    isChecked={filter.displayOnlyInsurers.some(x => x == insurer.insurerId)}
                    onChange={(e) => handleChangeFilterInsurer(insurer.insurerId, e.target.checked)}
                  >
                    {insurer.insurerName}
                  </Checkbox>
                )
              })}
            </CustomVStack>
          </FilterSectionGroup>
        }

        {dataRisks && dataRisks.data && dataRisksVariants && dataRisksVariants.data && Object.entries(dataSource.riskVariants ?? {}).map(([riskId, riskVariantIds]) => {
          const risk = dataRisks.data.find(x => x.id == Number(riskId))!
          if (risk?.value == Risk.SKLA.toString()) return

          const riskVariants = dataRisksVariants.data.filter(x => riskVariantIds!.includes(x.id)).sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))

          return (
            <FilterSectionGroup name={risk?.name} key={riskId}>
              <CustomVStack>
                {Number(riskId) == Risk.SPOL && dataSource.fixedComplicity && dataSource.percentageComplicity &&
                  <>
                    <Checkbox
                      size='sm'
                      isChecked={filter.displayOnlyFixedComplicity}
                      onChange={(e) => handleChangeFilterFixedComplicity(e.target.checked, undefined)}
                    >
                      {translate('fix', 'Fix')}
                    </Checkbox>

                    <Checkbox
                      size='sm'
                      isChecked={filter.displayOnlyPercentageComplicity}
                      onChange={(e) => handleChangeFilterFixedComplicity(undefined, e.target.checked)}
                    >
                      %
                    </Checkbox>
                    <Box></Box>
                  </>
                }

                {riskVariants.map(riskVariant => {
                  const name = riskVariant?.name?.replace(translate('complicity', 'Spoluúčast'), '')

                  return (
                    <Checkbox
                      key={riskVariant.id}
                      size='sm'
                      value={riskVariant.id.toString()}
                      isChecked={filter.displayOnlyRiskVariants[risk.id]?.some(x => x == riskVariant.id) ?? false}
                      onChange={(e) => handleChangeFilterRiskVariant(risk.id, riskVariant.id, e.target.checked)}
                    >
                      {name}
                    </Checkbox>
                  )
                })}

              </CustomVStack>
            </FilterSectionGroup>
          )
        })}

        {product == Product.VehicleLeasingInsurance && dataSource?.fixed && dataSource?.float && filtered &&
          <FilterSectionGroup name={translate('pricingType', 'Typ sazby')}>
            <CustomVStack>
              <Checkbox
                size='sm'
                isChecked={filter.displayOnlyFloat}
                onChange={(e) => handleChangeFilterFixed(undefined, e.target.checked)}
              >
                {translate('float', 'Float')}
              </Checkbox>

              <Checkbox
                size='sm'
                isChecked={filter.displayOnlyFixed}
                onChange={(e) => handleChangeFilterFixed(e.target.checked, undefined)}
              >
                {translate('fix', 'Fix')}
              </Checkbox>
            </CustomVStack>
          </FilterSectionGroup>
        }

        {dataRisks?.data && dataSource.addons &&
          <FilterSectionGroup name={translate('step2.filter.addons', 'Doplňková připojištění')}>
            {dataSource.addons.map(riskId => {
              const risk = dataRisks.data.find(x => x.id == riskId)!

              return (
                <Checkbox
                  key={riskId}
                  size='sm'
                  value={riskId.toString()}
                  isChecked={filter.displayOnlyRisks.some(x => x == risk.id)}
                  onChange={(e) => handleChangeFilterRisk(risk.id, e.target.checked)}
                >
                  {risk?.name}
                </Checkbox>
              )
            })
            }
          </FilterSectionGroup>
        }
      </Accordion>

      <Flex flexDir={'column'} gap={2.5} pl={2.5} pr={2} pt={2.5}>
        <Text fontSize={'sm'} fontWeight={'semibold'}>{translate('other', 'Ostatní')}</Text>

        {priceRange[0] !== priceRange[1] &&
          <Box sx={{ pl: 3, pr: 6, mb: 6, mt: 6 }}>
            <RangeSlider
              aria-label={['min', 'max']}
              step={100}
              min={priceRange.at(0)}
              max={priceRange.at(1)}
              value={priceRangeFilter}
              onChange={(range) => handleChangeFilterPriceRange(range[0], range[1])}
              title={`min. ${priceFormat(priceRange[0], 'N/A')}, max. ${priceFormat(priceRange[1], 'N/A')}`}
            >
              <RangeSliderTrack>
                <RangeSliderFilledTrack />
              </RangeSliderTrack>
              <RangeSliderThumb index={0}>
                <Text fontSize={'sm'} fontWeight={'bold'} sx={{ position: 'absolute', left: -1, bottom: 6 }}>{priceFormat(priceRangeFilter[0], 'N/A')}</Text>
              </RangeSliderThumb>
              <RangeSliderThumb index={1}>
                <Text fontSize={'sm'} fontWeight={'bold'} sx={{ position: 'absolute', right: -1, top: 6 }}>{priceFormat(priceRangeFilter[1], 'N/A')}</Text>
              </RangeSliderThumb>
            </RangeSlider>
          </Box>
        }

        {anyErrorQuote &&
          !printMode && // v print modu se chybove nezobrazuji
          <FormControl display='flex' alignItems='center'>
            <Switch id="showWithErrors"
              size='sm'
              isChecked={showWithErrors}
              onChange={() => setShowWithErrors(!showWithErrors)} />
            <FormLabel variant={'switch'} fontSize={'sm'} fontWeight={'medium'} htmlFor='showWithErrors' cursor='pointer'>{translate('step2.filter.showWithErrors', 'Zobrazit chybné kalkulace')}</FormLabel>
          </FormControl>
        }

        {anyHidden &&
          <FormControl display='flex' alignItems='center'>
            <Switch id="showAll"
              size='sm'
              isChecked={showAll}
              onChange={() => setShowAll(!showAll)} />
            <FormLabel variant={'switch'} fontSize={'sm'} fontWeight={'medium'} htmlFor='showAll' cursor='pointer'>{translate('step2.filter.showNotSelected', 'Zobrazit nevybrané')}</FormLabel>
          </FormControl>
        }
      </Flex>

      <Stack id='filterButtons' mx={4} gap={4} mt={6}>
        {filterAplied && <Button variant='link' colorScheme='gray' onClick={resetFilter} _focus={{ boxShadow: 'none' }}>{translate('step2.filter.resetFilter', 'Resetovat filtr')}</Button>}

        {printMode &&
          <PrintModeButtons
            offers={offers}
            filtered={filtered}
            addonOffers={addonOffers}
            activeAddonOffers={activeAddonOffers}
            updateOffer={(offer: Offer) => updateOffer(offer as OfferVehicle)}
            updateAddonOffer={(offer: Offer) => updateAddonOffer(offer as OfferAddon)}
            toPrintCount={toPrintCount}
            translate={translate}
          />
        }

      </Stack>
    </Grid >
  )
}

const mainRisks = [Risk.POV, Risk.HAV, Risk.SPOL]
export const getQuotesFilterDS = (offers: Offer[], variants?: Variant[], updateDsCallback?: (ds: FilterOptions, offer: Offer, variants?: Variant[]) => void) => {
  const offersFilterOptions = offers.map(o => getOfferFilterOptions(o))
  const ds = reduceToFilterOptions(offersFilterOptions)

  if (updateDsCallback)
    offers.forEach(offer => updateDsCallback(ds, offer, variants))

  return ds
}

const getOfferFilterOptions = (offer: Offer) => {
  const spolPart = offer.parts?.find(x => x.riskId === Risk.SPOL)

  const mainRisksRiskVariants = offer.parts
    ?.filter(x => mainRisks.includes(x.riskId) && x.defaultRiskVariantId)
    .map(x => ({ riskId: x.riskId, riskVariantId: x.defaultRiskVariantId! })) ?? []

  const addonRiskProductIds = offer.parts
    ?.filter(x => !mainRisks.includes(x.riskId) && x.riskId !== Risk.ASISTENCE)
    .map(x => ({ riskId: x.riskId })) ?? []

  return {
    insurer: { insurerId: offer.insurerId, insurerName: offer.insurerName },
    fixedComplicity: !!spolPart?.complicityFix,
    percentageComplicity: !!spolPart?.complicityPercentage,
    mainRisksRiskVariants,
    addonRiskProductIds
  }
}

type OfferFilterOptions = ReturnType<typeof getOfferFilterOptions>

const reduceToFilterOptions = (optionsList: OfferFilterOptions[]): FilterOptions =>
  optionsList.reduce<FilterOptions>((ds, { insurer, fixedComplicity, percentageComplicity, mainRisksRiskVariants, addonRiskProductIds }) => {
    if (!ds.insurers?.some(i => i.insurerId === insurer.insurerId))
      ds.insurers = [...(ds.insurers ?? []), insurer]

    if (fixedComplicity) ds.fixedComplicity = true
    if (percentageComplicity) ds.percentageComplicity = true

    mainRisksRiskVariants.forEach(({ riskId, riskVariantId }) => {
      ds.riskVariants ??= {}
      if (!ds.riskVariants[riskId]?.includes(riskVariantId))
        ds.riskVariants[riskId] = [...(ds.riskVariants[riskId] ?? []), riskVariantId]
    })

    addonRiskProductIds.forEach(({ riskId }) => {
      if (!ds.addons?.includes(riskId)) {
        ds.addons ??= []
        ds.addons.push(riskId)
      }
    })

    return ds
  }, {})